import React, { useMemo, useState } from "react";
import { Button, Empty, Input, InputNumber, Modal, Radio, Segmented, Select, Table, Tooltip, message } from "antd";
import dayjs from "dayjs";
import { AlertTriangle, Plus, Trash2, Wallet, Undo2, Ban, ReceiptText } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
    INVOICE_KIND_LABEL, INVOICE_STATUS, PAYMENT_METHOD, PRODUCT_CATALOG, appointmentsOf, callCenterApi, customer360, formatMoney,
    invoiceCollected, invoiceDue, invoiceNet, invoiceOfAppointment, invoiceRefunded, invoiceStatus, invoiceSubtotal, invoiceTotal,
    invoicesOf, servicePrice, useCallCenter,
    type Customer, type Invoice, type InvoiceItem, type InvoiceKind, type InvoiceStatus, type PaymentMethod,
} from "../../CustomerDemo/callCenterStore";
import { meName } from "../../CustomerDemo/softphone";

const moneyProps = {
    formatter: (v?: string | number) => `${v ?? ""}`.replace(/\B(?=(\d{3})+(?!\d))/g, "."),
    parser: (v?: string) => Number((v || "").replace(/\./g, "")),
    min: 0,
    step: 50000,
    addonAfter: "đ",
};

const StatusBadge = ({ status }: { status: InvoiceStatus }) => (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border whitespace-nowrap ${INVOICE_STATUS[status].badge}`}>{INVOICE_STATUS[status].label}</span>
);

const MethodPicker = ({ value, onChange }: { value: PaymentMethod; onChange: (m: PaymentMethod) => void }) => (
    <Radio.Group size="small" optionType="button" buttonStyle="solid" value={value} onChange={e => onChange(e.target.value)}
        options={(Object.keys(PAYMENT_METHOD) as PaymentMethod[]).map(k => ({ value: k, label: PAYMENT_METHOD[k].label }))} />
);

// ============================ Lập hoá đơn ============================

function CreateInvoiceModal({ customer, open, onClose, presetAppointment }: { customer: Customer; open: boolean; onClose: () => void; presetAppointment?: string }) {
    const s = useCallCenter();
    const pendingAppts = appointmentsOf(s, customer.id).filter(a => a.status === "done" && !invoiceOfAppointment(s, a.id));
    const [kind, setKind] = useState<InvoiceKind>("product");
    const [apptId, setApptId] = useState<string>();
    const [items, setItems] = useState<InvoiceItem[]>([]);
    const [discount, setDiscount] = useState<number>(0);
    const [payNow, setPayNow] = useState<number>(0);
    const [method, setMethod] = useState<PaymentMethod>("transfer");
    const [note, setNote] = useState("");

    // Mở modal → khởi tạo theo lịch được chọn sẵn (hoặc lịch đầu tiên chưa lập hoá đơn)
    React.useEffect(() => {
        if (!open) return;
        const ap = pendingAppts.find(a => a.id === presetAppointment) || (presetAppointment ? undefined : pendingAppts[0]);
        if (ap) { setKind("service"); pickAppt(ap.id); } else { setKind("product"); setApptId(undefined); setItems([{ name: PRODUCT_CATALOG[0].name, qty: 1, price: PRODUCT_CATALOG[0].price }]); }
        setDiscount(0); setPayNow(0); setMethod("transfer"); setNote("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, presetAppointment]);

    function pickAppt(id?: string) {
        setApptId(id);
        const ap = pendingAppts.find(a => a.id === id);
        setItems(ap ? [{ name: ap.service, qty: 1, price: ap.price ?? servicePrice(ap.service) }] : []);
    }
    const switchKind = (k: InvoiceKind) => {
        setKind(k);
        if (k === "service") pickAppt(pendingAppts[0]?.id);
        else { setApptId(undefined); setItems([{ name: PRODUCT_CATALOG[0].name, qty: 1, price: PRODUCT_CATALOG[0].price }]); }
    };
    const setItem = (i: number, patch: Partial<InvoiceItem>) => setItems(prev => prev.map((x, j) => (j === i ? { ...x, ...patch } : x)));
    const draft = { items, discount };
    const subtotal = invoiceSubtotal(draft);
    const total = invoiceTotal(draft);
    const problem = !items.length ? "Hoá đơn phải có ít nhất 1 dòng"
        : kind === "service" && !apptId ? "Chọn lần làm dịch vụ"
            : discount > subtotal ? "Giảm giá vượt quá tạm tính"
                : payNow > total ? "Số tiền thu vượt tổng hoá đơn" : "";

    const save = () => {
        const r = callCenterApi.createInvoice({
            customerId: customer.id, kind, items, discount, appointmentId: kind === "service" ? apptId : undefined, by: meName(), note,
            payment: payNow > 0 ? { amount: payNow, method } : null,
        });
        if (r.error) { message.error(r.error); return; }
        message.success(payNow >= total ? "Đã lập hoá đơn và thu đủ" : payNow > 0 ? "Đã lập hoá đơn, thu một phần" : "Đã lập hoá đơn (chờ thu tiền)");
        onClose();
    };

    return (
        <Modal open={open} onCancel={onClose} width={640} title="Lập hoá đơn"
            footer={[<Button key="c" onClick={onClose}>Huỷ</Button>, <Button key="s" type="primary" disabled={!!problem} onClick={save}>Lưu hoá đơn</Button>]}>
            <div className="space-y-4 pt-1 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                    <Radio.Group optionType="button" buttonStyle="solid" value={kind} onChange={e => switchKind(e.target.value)}
                        options={[{ value: "service", label: "Dịch vụ" }, { value: "product", label: "Sản phẩm" }]} />
                    {kind === "service" && (
                        <Select className="min-w-[280px] flex-1" value={apptId} onChange={pickAppt} placeholder="Chọn lần làm dịch vụ"
                            notFoundContent="Không có lần làm dịch vụ nào chờ lập hoá đơn"
                            options={pendingAppts.map(a => ({ value: a.id, label: `${dayjs(a.at).format("DD/MM/YYYY")} · ${a.service} · ${a.branch}` }))} />
                    )}
                </div>
                {kind === "service" && !pendingAppts.length && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-center gap-1.5">
                        <AlertTriangle size={13} /> Hoá đơn dịch vụ chỉ lập cho lịch hẹn "Đã thực hiện" chưa có hoá đơn.
                    </div>
                )}

                <div className="space-y-2">
                    {items.map((it, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2">
                            {kind === "product" ? (
                                <Select className="flex-1 min-w-[200px]" value={it.name} showSearch
                                    options={PRODUCT_CATALOG.map(p => ({ value: p.name, label: `${p.name} · ${formatMoney(p.price)}` }))}
                                    onChange={v => setItem(i, { name: v, price: PRODUCT_CATALOG.find(p => p.name === v)?.price ?? it.price })} />
                            ) : <span className="flex-1 min-w-[200px] font-medium text-gray-700 dark:text-gray-200">{it.name}</span>}
                            <InputNumber min={1} max={99} value={it.qty} onChange={v => setItem(i, { qty: v || 1 })} className="!w-20" disabled={kind === "service"} />
                            <InputNumber {...moneyProps} value={it.price} onChange={v => setItem(i, { price: Number(v) || 0 })} className="!w-48" />
                            {kind === "product" && items.length > 1 && (
                                <Button type="text" danger icon={<Trash2 size={14} />} onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} />
                            )}
                        </div>
                    ))}
                    {kind === "product" && (
                        <Button size="small" type="dashed" icon={<Plus size={13} />} onClick={() => setItems(prev => [...prev, { name: PRODUCT_CATALOG[0].name, qty: 1, price: PRODUCT_CATALOG[0].price }])}>Thêm sản phẩm</Button>
                    )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 border-t border-gray-100 dark:border-gray-800 pt-3">
                    <div className="space-y-1">
                        <div className="text-gray-500">Tạm tính: <b className="text-gray-800 dark:text-gray-100">{formatMoney(subtotal)}</b></div>
                        <div className="flex items-center gap-2 text-gray-500">Giảm giá <InputNumber {...moneyProps} max={subtotal} value={discount} onChange={v => setDiscount(Number(v) || 0)} className="!w-44" size="small" /></div>
                        <div className="text-base">Tổng tiền: <b className="text-[#3e91ab]">{formatMoney(total)}</b></div>
                    </div>
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-gray-500">Thu ngay <InputNumber {...moneyProps} max={total} value={payNow} onChange={v => setPayNow(Number(v) || 0)} className="!w-44" size="small" /></div>
                        <div className="flex items-center gap-2">
                            <Button size="small" onClick={() => setPayNow(total)}>Thu đủ</Button>
                            {payNow > 0 && <MethodPicker value={method} onChange={setMethod} />}
                        </div>
                        {payNow > 0 && payNow < total && <div className="text-xs text-amber-600">Còn nợ {formatMoney(total - payNow)} — sẽ hiện ở "Việc cần làm"</div>}
                    </div>
                </div>
                <Input.TextArea value={note} onChange={e => setNote(e.target.value)} autoSize={{ minRows: 1, maxRows: 3 }} placeholder="Ghi chú hoá đơn (tuỳ chọn)" />
                {problem && <div className="text-xs text-rose-500">{problem}</div>}
            </div>
        </Modal>
    );
}

// ============================ Thu tiền / Hoàn tiền / Huỷ ============================

type ActionMode = "pay" | "refund" | "cancel";

function InvoiceActionModal({ invoice, mode, onClose }: { invoice: Invoice | null; mode: ActionMode; onClose: () => void }) {
    const [amount, setAmount] = useState(0);
    const [method, setMethod] = useState<PaymentMethod>("transfer");
    const [reason, setReason] = useState("");
    React.useEffect(() => {
        if (!invoice) return;
        setAmount(mode === "pay" ? invoiceDue(invoice) : mode === "refund" ? invoiceNet(invoice) : 0);
        setMethod("transfer");
        setReason("");
    }, [invoice, mode]);
    if (!invoice) return null;
    const due = invoiceDue(invoice);
    const net = invoiceNet(invoice);
    const quick = mode === "cancel"
        ? ["Lập nhầm hoá đơn", "Khách đổi sang gói khác", "Trùng hoá đơn"]
        : ["Khách không hài lòng kết quả", "Thu thừa tiền", "Khách đổi ý, trả lại sản phẩm"];
    const submit = () => {
        const err = mode === "pay" ? callCenterApi.addPayment(invoice.id, amount, method, meName(), reason)
            : mode === "refund" ? callCenterApi.refundInvoice(invoice.id, amount, reason, method, meName())
                : callCenterApi.cancelInvoice(invoice.id, reason, meName());
        if (err) { message.error(err); return; }
        message.success(mode === "pay" ? (amount >= due ? "Đã thu đủ" : `Đã thu ${formatMoney(amount)}`) : mode === "refund" ? "Đã hoàn tiền" : "Đã huỷ hoá đơn");
        onClose();
    };
    const title = mode === "pay" ? `Thu tiền ${invoice.code}` : mode === "refund" ? `Hoàn tiền ${invoice.code}` : `Huỷ hoá đơn ${invoice.code}`;
    const needReason = mode !== "pay";

    return (
        <Modal open onCancel={onClose} width={480} title={title}
            footer={[<Button key="c" onClick={onClose}>Đóng</Button>,
            <Button key="s" type="primary" danger={mode !== "pay"} disabled={(needReason && !reason.trim()) || (mode !== "cancel" && !(amount > 0))} onClick={submit}>
                {mode === "pay" ? "Xác nhận thu" : mode === "refund" ? "Xác nhận hoàn" : "Huỷ hoá đơn"}
            </Button>]}>
            <div className="space-y-3 text-sm pt-1">
                <div className="grid grid-cols-3 gap-2 text-center">
                    {[["Tổng tiền", invoiceTotal(invoice)], ["Đã thu", net], ["Còn nợ", due]].map(([l, v]) => (
                        <div key={l as string} className="border border-gray-200 dark:border-gray-700 rounded-lg py-2">
                            <div className="text-[11px] text-gray-500">{l}</div>
                            <div className="font-semibold">{formatMoney(v as number)}</div>
                        </div>
                    ))}
                </div>
                {mode !== "cancel" && (
                    <>
                        <div className="flex items-center gap-2">
                            <span className="w-24 text-gray-500">{mode === "pay" ? "Số tiền thu" : "Số tiền hoàn"}</span>
                            <InputNumber {...moneyProps} max={mode === "pay" ? due : net} value={amount} onChange={v => setAmount(Number(v) || 0)} className="flex-1" />
                        </div>
                        <div className="flex items-center gap-2"><span className="w-24 text-gray-500">Hình thức</span><MethodPicker value={method} onChange={setMethod} /></div>
                    </>
                )}
                {mode === "refund" && <div className="text-xs text-amber-600 flex items-center gap-1.5"><AlertTriangle size={13} /> Sau khi hoàn tiền, hoá đơn được chốt và không thu thêm.</div>}
                {mode === "cancel" && <div className="text-xs text-amber-600 flex items-center gap-1.5"><AlertTriangle size={13} /> Chỉ huỷ được hoá đơn chưa thu tiền. Hoá đơn dịch vụ bị huỷ có thể lập lại.</div>}
                <div>
                    <div className="text-gray-500 mb-1">{needReason ? <>Lý do <span className="text-rose-500">*</span></> : "Ghi chú"}</div>
                    <Input.TextArea value={reason} onChange={e => setReason(e.target.value)} autoSize={{ minRows: 2, maxRows: 4 }}
                        placeholder={needReason ? "Bắt buộc nhập lý do" : "VD: Khách chuyển khoản, mã giao dịch..."} />
                    {needReason && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {quick.map(q => <button key={q} type="button" onClick={() => setReason(q)} className="text-xs px-2 py-1 rounded-full border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#3e91ab] cursor-pointer">{q}</button>)}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
}

// ============================ Chi tiết 1 hoá đơn ============================

function InvoiceDetail({ inv }: { inv: Invoice }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm py-1">
            <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Chi tiết</div>
                <table className="w-full">
                    <tbody>
                        {inv.items.map((x, i) => (
                            <tr key={i} className="border-b border-gray-100 dark:border-gray-800">
                                <td className="py-1">{x.name}</td>
                                <td className="py-1 text-right text-gray-500 w-16">× {x.qty}</td>
                                <td className="py-1 text-right tabular-nums w-32">{formatMoney(x.qty * x.price)}</td>
                            </tr>
                        ))}
                        {inv.discount > 0 && <tr><td className="py-1 text-gray-500">Giảm giá</td><td /><td className="py-1 text-right tabular-nums text-rose-500">−{formatMoney(inv.discount)}</td></tr>}
                        <tr><td className="py-1 font-semibold">Tổng tiền</td><td /><td className="py-1 text-right tabular-nums font-semibold">{formatMoney(invoiceTotal(inv))}</td></tr>
                    </tbody>
                </table>
                {inv.note && <div className="text-xs text-gray-500 mt-2">Ghi chú: {inv.note}</div>}
                {inv.cancelled && <div className="text-xs text-rose-500 mt-2">Đã huỷ bởi {inv.cancelled.by} lúc {dayjs(inv.cancelled.at).format("HH:mm DD/MM/YYYY")} — {inv.cancelled.reason}</div>}
            </div>
            <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1.5">Lịch sử thu / hoàn tiền</div>
                {inv.payments.length ? (
                    <div className="space-y-1.5">
                        {inv.payments.map(p => (
                            <div key={p.id} className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className={p.amount < 0 ? "text-purple-600" : "text-gray-700 dark:text-gray-200"}>{p.amount < 0 ? "Hoàn tiền" : "Thu tiền"} · {PAYMENT_METHOD[p.method].label}</div>
                                    <div className="text-[11px] text-gray-400">{p.by} · {dayjs(p.at).format("HH:mm DD/MM/YYYY")}{p.note ? ` · ${p.note}` : ""}</div>
                                </div>
                                <span className={`tabular-nums font-medium shrink-0 ${p.amount < 0 ? "text-purple-600" : "text-emerald-600"}`}>{p.amount < 0 ? "−" : "+"}{formatMoney(Math.abs(p.amount))}</span>
                            </div>
                        ))}
                    </div>
                ) : <div className="text-gray-400 italic">Chưa thu tiền</div>}
            </div>
        </div>
    );
}

// ============================ Tab chính ============================

/** Lịch sử hoá đơn của khách: tổng chi tiêu, công nợ, biểu đồ theo năm, danh sách + thu tiền / hoàn / huỷ */
export default function InvoiceHistory({ customer }: { customer: Customer }) {
    const s = useCallCenter();
    const all = invoicesOf(s, customer.id);
    const k = customer360(s, customer);
    const pendingAppts = appointmentsOf(s, customer.id).filter(a => a.status === "done" && !invoiceOfAppointment(s, a.id));
    const years = useMemo(() => Array.from(new Set([dayjs().year(), ...all.map(x => dayjs(x.createdAt).year())])).sort((a, b) => b - a), [all]);
    const [year, setYear] = useState(dayjs().year());
    const [hidden, setHidden] = useState<InvoiceKind[]>([]);
    const [tab, setTab] = useState<"all" | InvoiceKind>("all");
    const [status, setStatus] = useState<InvoiceStatus | "open" | "all">("all");
    const [creating, setCreating] = useState<{ appt?: string } | null>(null);
    const [action, setAction] = useState<{ inv: Invoice; mode: ActionMode } | null>(null);

    const valid = all.filter(x => !x.cancelled);
    const byKind = (kind: InvoiceKind) => valid.filter(x => x.kind === kind);
    const sum = (xs: Invoice[], f: (x: Invoice) => number) => xs.reduce((t, x) => t + f(x), 0);
    const refunded = sum(valid, invoiceRefunded);

    const chart = Array.from({ length: 12 }, (_, m) => {
        const inMonth = valid.filter(x => dayjs(x.createdAt).year() === year && dayjs(x.createdAt).month() === m);
        return { name: `Th ${m + 1}`, service: inMonth.filter(x => x.kind === "service").length, product: inMonth.filter(x => x.kind === "product").length };
    });

    const rows = all.filter(x => (tab === "all" || x.kind === tab)
        && (status === "all" || (status === "open" ? invoiceDue(x) > 0 : invoiceStatus(x) === status)));

    return (
        <div className="space-y-4">
            {pendingAppts.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900 px-4 py-2.5 flex flex-wrap items-center gap-3 text-sm">
                    <AlertTriangle size={15} className="text-amber-600" />
                    <span className="flex-1 min-w-[220px] text-amber-800 dark:text-amber-300">
                        Có {pendingAppts.length} lần làm dịch vụ chưa lập hoá đơn: {pendingAppts.map(a => `${a.service} ${dayjs(a.at).format("DD/MM")}`).join(", ")}
                    </span>
                    <Button size="small" type="primary" onClick={() => setCreating({ appt: pendingAppts[0].id })}>Lập hoá đơn</Button>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="text-center text-gray-500 text-sm">Tổng chi tiêu (đã thu, trừ hoàn)</div>
                    <div className="text-center text-2xl font-bold text-gray-900 dark:text-gray-100">{formatMoney(k.spend)}</div>
                    <div className="text-center mb-3"><span className={`px-2 py-0.5 rounded text-xs font-bold ${k.tier.cls}`}>Hạng {k.tier.label}</span></div>
                    <div className="grid grid-cols-2 text-center border-t border-gray-100 dark:border-gray-800">
                        <div className="py-3 border-r border-gray-100 dark:border-gray-800"><div className="text-lg font-semibold">{byKind("service").length}</div><div className="text-xs text-gray-500">HĐ dịch vụ</div></div>
                        <div className="py-3"><div className="text-lg font-semibold">{byKind("product").length}</div><div className="text-xs text-gray-500">HĐ sản phẩm</div></div>
                    </div>
                    <div className="grid grid-cols-2 text-center border-t border-gray-100 dark:border-gray-800">
                        <div className="py-3 border-r border-gray-100 dark:border-gray-800"><div className="font-semibold">{formatMoney(sum(byKind("service"), invoiceNet))}</div><div className="text-xs text-gray-500">Đã thu dịch vụ</div></div>
                        <div className="py-3"><div className="font-semibold">{formatMoney(sum(byKind("product"), invoiceNet))}</div><div className="text-xs text-gray-500">Đã thu sản phẩm</div></div>
                    </div>
                    <div className="grid grid-cols-2 text-center border-t border-gray-100 dark:border-gray-800">
                        <div className="py-3 border-r border-gray-100 dark:border-gray-800"><div className={`font-semibold ${k.debt ? "text-rose-600" : ""}`}>{formatMoney(k.debt)}</div><div className="text-xs text-gray-500">Còn nợ</div></div>
                        <div className="py-3"><div className="font-semibold">{formatMoney(refunded)}</div><div className="text-xs text-gray-500">Đã hoàn</div></div>
                    </div>
                </div>

                <div className="xl:col-span-2 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                    <div className="flex justify-between items-center mb-3 gap-2">
                        <div className="font-medium text-gray-800 dark:text-gray-100">Số hoá đơn theo tháng</div>
                        <Select size="small" className="w-[100px]" value={year} onChange={setYear} options={years.map(y => ({ value: y, label: String(y) }))} />
                    </div>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={chart} margin={{ top: 5, right: 10, left: -30, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                            <XAxis dataKey="name" interval={0} tick={{ fontSize: 11 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                            <ChartTooltip formatter={(v: number, n: string) => [v, n === "service" ? "Hoá đơn dịch vụ" : "Hoá đơn sản phẩm"]} />
                            {!hidden.includes("service") && <Line dataKey="service" stroke="#3b82f6" strokeWidth={2} />}
                            {!hidden.includes("product") && <Line dataKey="product" stroke="#9ca3af" strokeWidth={2} />}
                        </LineChart>
                    </ResponsiveContainer>
                    <div className="flex gap-4 justify-center mt-1 text-sm">
                        {(["service", "product"] as InvoiceKind[]).map(kd => (
                            <button key={kd} type="button" onClick={() => setHidden(h => (h.includes(kd) ? h.filter(x => x !== kd) : [...h, kd]))}
                                className={`cursor-pointer flex items-center gap-1.5 ${hidden.includes(kd) ? "opacity-40" : ""}`}>
                                <span className={`w-3 h-3 inline-block rounded-sm ${kd === "service" ? "bg-blue-500" : "bg-gray-400"}`} /> Hoá đơn {INVOICE_KIND_LABEL[kd].toLowerCase()}
                            </button>
                        ))}
                    </div>
                    {!chart.some(m => m.service || m.product) && <div className="text-center text-xs text-gray-400 -mt-1">Năm {year} chưa có hoá đơn</div>}
                </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <Segmented value={tab} onChange={v => setTab(v as typeof tab)} options={[
                    { value: "all", label: `Tất cả (${all.length})` },
                    { value: "service", label: `Dịch vụ (${all.filter(x => x.kind === "service").length})` },
                    { value: "product", label: `Sản phẩm (${all.filter(x => x.kind === "product").length})` },
                ]} />
                <div className="flex items-center gap-2">
                    <Select className="w-44" value={status} onChange={setStatus} options={[
                        { value: "all", label: "Mọi trạng thái" },
                        { value: "open", label: "Còn nợ" },
                        ...(Object.keys(INVOICE_STATUS) as InvoiceStatus[]).map(x => ({ value: x, label: INVOICE_STATUS[x].label })),
                    ]} />
                    <Button type="primary" icon={<Plus size={14} />} onClick={() => setCreating({})}>Lập hoá đơn</Button>
                </div>
            </div>

            <Table<Invoice> rowKey="id" dataSource={rows} pagination={rows.length > 10 ? { pageSize: 10 } : false} size="middle" scroll={{ x: "max-content" }}
                className="custom-ant-table"
                rowClassName={x => (x.cancelled ? "opacity-60" : "")}
                expandable={{ expandedRowRender: inv => <InvoiceDetail inv={inv} />, expandRowByClick: true }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={all.length ? "Không có hoá đơn khớp bộ lọc" : "Khách chưa có hoá đơn nào"} /> }}
                columns={[
                    {
                        title: "Mã HĐ", key: "code", render: (_, x) => (
                            <div className="flex items-center gap-1.5">
                                <ReceiptText size={14} className="text-gray-400" />
                                <span className="text-blue-600 font-medium">{x.code}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${x.kind === "service" ? "bg-blue-50 text-blue-600" : "bg-gray-100 text-gray-600"}`}>{INVOICE_KIND_LABEL[x.kind]}</span>
                            </div>
                        ),
                    },
                    { title: "Ngày lập", key: "at", render: (_, x) => <div><div>{dayjs(x.createdAt).format("HH:mm DD/MM/YYYY")}</div><div className="text-xs text-gray-400">{x.by}</div></div> },
                    { title: "Chi nhánh", dataIndex: "branch", render: (v: string) => v || "—" },
                    {
                        title: "Nội dung", key: "items", render: (_, x) => {
                            const ap = x.appointmentId ? s.appointments.find(a => a.id === x.appointmentId) : null;
                            return (
                                <div className="max-w-[260px]">
                                    <div className="truncate" title={x.items.map(i => `${i.name} ×${i.qty}`).join(", ")}>{x.items.map(i => (i.qty > 1 ? `${i.name} ×${i.qty}` : i.name)).join(", ")}</div>
                                    {ap && <div className="text-xs text-gray-400">Lần làm DV {dayjs(ap.at).format("DD/MM/YYYY")}</div>}
                                </div>
                            );
                        },
                    },
                    { title: "Tổng tiền", key: "total", align: "right", render: (_, x) => <div className="tabular-nums">{formatMoney(invoiceTotal(x))}{x.discount > 0 && <div className="text-[11px] text-gray-400">Giảm {formatMoney(x.discount)}</div>}</div> },
                    {
                        title: "Đã thu", key: "paid", align: "right", render: (_, x) => (
                            <div className="tabular-nums text-emerald-600">{formatMoney(invoiceNet(x))}{invoiceRefunded(x) > 0 && <div className="text-[11px] text-purple-500">Hoàn {formatMoney(invoiceRefunded(x))}</div>}</div>
                        ),
                    },
                    { title: "Còn nợ", key: "due", align: "right", render: (_, x) => <span className={`tabular-nums ${invoiceDue(x) ? "text-rose-600 font-semibold" : "text-gray-300"}`}>{invoiceDue(x) ? formatMoney(invoiceDue(x)) : "—"}</span> },
                    { title: "Trạng thái", key: "st", render: (_, x) => <StatusBadge status={invoiceStatus(x)} /> },
                    {
                        title: "", key: "act", fixed: "right", render: (_, x) => {
                            const st = invoiceStatus(x);
                            const closed = st === "cancelled" || st === "refunded";
                            return (
                                <div className="flex items-center justify-end gap-0.5 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                                    {invoiceDue(x) > 0 && <Button size="small" type="link" icon={<Wallet size={13} />} onClick={() => setAction({ inv: x, mode: "pay" })}>Thu tiền</Button>}
                                    {!closed && invoiceNet(x) > 0 && <Button size="small" type="link" className="!text-purple-600" icon={<Undo2 size={13} />} onClick={() => setAction({ inv: x, mode: "refund" })}>Hoàn</Button>}
                                    {!x.cancelled && (
                                        <Tooltip title={invoiceCollected(x) > 0 ? "Đã thu tiền — dùng Hoàn tiền thay vì huỷ" : undefined}>
                                            <Button size="small" type="link" danger icon={<Ban size={13} />} disabled={invoiceCollected(x) > 0} onClick={() => setAction({ inv: x, mode: "cancel" })}>Huỷ</Button>
                                        </Tooltip>
                                    )}
                                </div>
                            );
                        },
                    },
                ]} />

            <CreateInvoiceModal customer={customer} open={!!creating} presetAppointment={creating?.appt} onClose={() => setCreating(null)} />
            {action && <InvoiceActionModal invoice={action.inv} mode={action.mode} onClose={() => setAction(null)} />}
        </div>
    );
}
